export type ChartDisplayContext = 'preview' | 'dashboard' | 'leader';

export interface ChartMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

type ChartLayoutSettings = Record<string, unknown> | null | undefined;

let measurementCanvas: HTMLCanvasElement | undefined;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function finiteNumberOr(value: unknown, fallback: number): number {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

export function getContextualChartMargins(
  settings: ChartLayoutSettings,
  displayContext: ChartDisplayContext,
  showLegend: boolean,
  fallback: ChartMargins
): ChartMargins {
  const resolvedSettings = settings ?? {};
  const normalizedContext = displayContext === 'leader' ? 'leader' : 'dashboard';

  const presetKey =
    normalizedContext === 'leader'
      ? showLegend
        ? 'leaderLegendMargins'
        : 'leaderBaseMargins'
      : showLegend
        ? 'dashboardLegendMargins'
        : 'dashboardBaseMargins';

  const preset = resolvedSettings[presetKey];
  if (isRecord(preset)) {
    return {
      top: finiteNumberOr(preset.top, fallback.top),
      right: finiteNumberOr(preset.right, fallback.right),
      bottom: finiteNumberOr(preset.bottom, fallback.bottom),
      left: finiteNumberOr(preset.left, fallback.left),
    };
  }

  const legacy =
    normalizedContext === 'leader'
      ? showLegend
        ? resolvedSettings.leaderLegendMargins ?? resolvedSettings.legendMargins
        : resolvedSettings.leaderBaseMargins ?? resolvedSettings.baseMargins
      : showLegend
        ? resolvedSettings.legendMargins
        : resolvedSettings.baseMargins;

  if (isRecord(legacy)) {
    return {
      top: finiteNumberOr(legacy.top, fallback.top),
      right: finiteNumberOr(legacy.right, fallback.right),
      bottom: finiteNumberOr(legacy.bottom, fallback.bottom),
      left: finiteNumberOr(legacy.left, fallback.left),
    };
  }

  return {
    top: finiteNumberOr(resolvedSettings.marginTop, fallback.top),
    right: finiteNumberOr(resolvedSettings.marginRight, fallback.right),
    bottom: finiteNumberOr(resolvedSettings.marginBottom, fallback.bottom),
    left: finiteNumberOr(resolvedSettings.marginLeft, fallback.left),
  };
}

export function measureChartTextWidth(
  text: string,
  font: string,
  fallbackCharacterWidth: number
): number {
  if (typeof document === 'undefined') return text.length * fallbackCharacterWidth;

  measurementCanvas ??= document.createElement('canvas');
  const context = measurementCanvas.getContext('2d');
  if (!context) return text.length * fallbackCharacterWidth;

  context.font = font;
  return context.measureText(text).width;
}
